/**
 * Debounced single-flight portfolio refresh.
 * Prevents loading spinners stuck when watch triggers arrive faster than RPC latency.
 */

export class PortfolioLoader {
  constructor({ setLoading, debounceMs = 120 } = {}) {
    this.setLoading = setLoading
    this.debounceMs = debounceMs
    this.generation = 0
    this.timer = null
    this.running = false
    this.pending = false
  }

  /** Queue a refresh; coalesces bursts within debounceMs. */
  schedule(fetchFn) {
    this.pending = true
    clearTimeout(this.timer)
    this.timer = setTimeout(() => {
      this.timer = null
      void this._drain(fetchFn)
    }, this.debounceMs)
  }

  async _drain(fetchFn) {
    if (this.running) return
    this.running = true
    try {
      while (this.pending) {
        this.pending = false
        const gen = ++this.generation
        this.setLoading(true)
        try {
          await fetchFn(gen, () => gen !== this.generation)
        } catch (err) {
          console.warn('[hieswap] portfolio refresh failed', err)
        } finally {
          if (gen === this.generation) this.setLoading(false)
        }
      }
    } finally {
      this.running = false
      if (this.pending) void this._drain(fetchFn)
    }
  }

  /** Test helper: flush debounce immediately. */
  async flush(fetchFn) {
    clearTimeout(this.timer)
    this.timer = null
    this.pending = true
    await this._drain(fetchFn)
  }
}

/** Simulate rapid triggers; returns final loading state after all work settles. */
export async function simulateRapidPortfolioTriggers({
  setLoading,
  getLoading,
  fetchFn,
  triggerCount = 20,
  triggerIntervalMs = 5,
  debounceMs = 120,
} = {}) {
  const loader = new PortfolioLoader({ setLoading, debounceMs })
  let resolves = 0
  const mockFetch = async (gen, isStale) => {
    await new Promise((r) => setTimeout(r, 30))
    if (!isStale()) resolves += 1
  }

  for (let i = 0; i < triggerCount; i++) {
    loader.schedule(mockFetch)
    if (triggerIntervalMs > 0) {
      await new Promise((r) => setTimeout(r, triggerIntervalMs))
    }
  }
  await loader.flush(mockFetch)
  return { loading: getLoading(), resolves }
}
